import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface SelectableItem {
  id: number | string;
  label: string;
  description?: string;
}

interface MultiSelectDialogProps {
  items: SelectableItem[];
  selectedIds: (number | string)[];
  onChange: (selectedIds: (number | string)[]) => void;
  onOpenChange?: (pipelineId?: number | string) => void;
  resourceId?: number | string;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  buttonClassName?: string;
  innerClassName?: string;
  icon?: React.ReactNode;
  compact?: boolean;
}

/**
 * Generic multi-select dialog component
 * Reusable for any M2M relationship management
 */
export function MultiSelectDialog({
  items,
  selectedIds,
  onChange,
  onOpenChange,
  resourceId,
  disabled = false,
  placeholder = "Select items...",
  searchPlaceholder = "Search...",
  emptyMessage = "No items found.",
  className,
  buttonClassName,
  innerClassName,
  icon,
  compact = false,
}: MultiSelectDialogProps) {
  const [open, setOpen] = useState(false);

  const selectedItems = items.filter((item) => selectedIds.includes(item.id));

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen && onOpenChange && resourceId !== undefined) {
      onOpenChange(resourceId);
    }
  };

  const toggleItem = (itemId: number | string) => {
    const newSelection = selectedIds.includes(itemId)
      ? selectedIds.filter((id) => id !== itemId)
      : [...selectedIds, itemId];
    onChange(newSelection);
  };

  return (
    <div className={cn("space-y-2", className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          {compact ? (
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-label={`${selectedItems.length} selected`}
              className={cn("h-8 w-full justify-between px-2", buttonClassName)}
              disabled={disabled}
            >
              <div className="flex items-center gap-1">
                {icon && <div className="shrink-0">{icon}</div>}
                <span className="text-xs">{selectedItems.length}</span>
              </div>
              <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
            </Button>
          ) : (
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn(
                "h-auto min-h-[2.25rem] w-full justify-between py-1.5",
                buttonClassName
              )}
              disabled={disabled}
            >
              <div className={cn("flex flex-1 flex-wrap items-center gap-1.5", innerClassName)}>
                {icon && <div className="shrink-0">{icon}</div>}
                {selectedItems.length === 0 ? (
                  <span className="text-sm text-muted-foreground">{placeholder}</span>
                ) : (
                  selectedItems.map((item) => (
                    <Badge
                      key={item.id}
                      variant="secondary"
                      className="gap-1 pr-1 font-normal"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleItem(item.id);
                      }}
                    >
                      <span className="max-w-[200px] truncate">{item.label}</span>
                      <X className="h-3 w-3 shrink-0 cursor-pointer hover:text-destructive" />
                    </Badge>
                  ))
                )}
              </div>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup>
                {items.map((item) => {
                  const isSelected = selectedIds.includes(item.id);
                  return (
                    <CommandItem
                      key={item.id}
                      value={item.label}
                      onSelect={() => toggleItem(item.id)}
                    >
                      <div
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible"
                        )}
                      >
                        <Check className="h-3 w-3" />
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium">{item.label}</span>
                        {item.description && (
                          <span className="text-xs text-muted-foreground">{item.description}</span>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
